import React, { useContext, type ReactNode } from 'react';

import { QuestionCircleFilled } from '@ant-design/icons';
import { Breadcrumb, Button, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { adminMenuMeta } from '@components/admin/menuConfig';
import { operatorMenuMeta } from '@components/operator/menuConfig';
import { TourContext } from '@contexts/TourContext';

export type PageHeaderItem = string | { label: ReactNode; path?: string };

interface PageHeaderProps {
  items?: PageHeaderItem[];
  basePath?: string;
  extra?: ReactNode;
  guideButton?: ReactNode;
}

interface MenuMetaEntry {
  path?: string;
  parentKey?: string;
  labelKey?: string;
  [key: string]: unknown;
}

/**
 * 頁面標頭元件（含麵包屑導航）
 *
 * @param {Object} props
 * @param {Array} props.items - 麵包屑項目，格式：
 *   - string: 使用 operator.menu 的 i18n key（如 'system_settings.shipping_accounts'）
 *   - object: { label: '自訂文字', path: '/custom/path' }
 * @param {string} props.basePath - 基礎路徑，預設 '/operator'
 *
 * @example
 * // 簡單用法
 * <PageHeader items={['system_settings', 'shipping_accounts']} />
 *
 * // 自訂項目
 * <PageHeader items={[
 *   { label: '庫存', path: '/operator/inventory' },
 *   'shelves'
 * ]} />
 */
const PageHeader: React.FC<PageHeaderProps> = ({ items = [], basePath = '/operator', extra, guideButton: guideButtonProp }) => {
  const { t } = useTranslation();
  const { steps, setIsOpen } = useContext(TourContext) as { steps?: unknown[]; setIsOpen: (open: boolean) => void };
  const hasTour = steps && steps.length > 0;

  // guideButton: 外部傳入優先，否則 fallback 到 Tour 按鈕
  const guideButton = guideButtonProp || (hasTour ? (
    <Tooltip title={t('common.tour.start', { defaultValue: 'Start Page Tour' }) as string}>
      <Button
        type="text"
        size="small"
        icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
        onClick={() => setIsOpen(true)}
        style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
      />
    </Tooltip>
  ) : null);

  // 根據 basePath 決定 i18n namespace 和 menuMeta
  const menuNamespace = basePath === '/admin' ? 'admin' : 'operator';
  const menuMeta = (basePath === '/admin' ? adminMenuMeta : operatorMenuMeta) as Record<string, MenuMetaEntry>;

  const breadcrumbItems = [
    {
      title: (
        <Link to={basePath} style={{ color: '#666' }}>
          {t('common.home')}
        </Link>
      ),
    },
    ...items.map((item, index) => {
      const isLast = index === items.length - 1;

      if (typeof item === 'string') {
        const meta = menuMeta[item];
        // 優先使用 menuMeta 中註冊的 labelKey（與 menuConfig 一致），
        // 否則回退到以 menu key 推導的 i18n path，最後 fallback 到 raw key。
        const label = meta?.labelKey
          ? t(meta.labelKey, { defaultValue: item })
          : t(`${menuNamespace}.menu.${item}.title`, {
              defaultValue: t(`${menuNamespace}.menu.${item}`, { defaultValue: item })
            });

        // 從 menuMeta 獲取路徑，非最後一項且有子頁面的才加連結
        // 父層級選單（如 production）沒有實際頁面，不應加連結
        const path = meta?.path;
        const hasParent = !!meta?.parentKey; // 有 parentKey 表示是子選單，有實際頁面

        if (path && !isLast && hasParent) {
          return {
            title: (
              <Link to={path} style={{ color: '#666' }}>
                {label as ReactNode}
              </Link>
            ),
          };
        }

        return {
          title: (
            <span style={{
              color: isLast ? '#333' : '#666',
              fontWeight: isLast ? 600 : 'normal',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}>
              {label as ReactNode}
              {isLast && guideButton}
            </span>
          ),
        };
      } else {
        // 物件格式：自訂 label 和 path
        const { label, path } = item;

        if (path && !isLast) {
          return {
            title: (
              <Link to={path} style={{ color: '#666' }}>
                {label}
              </Link>
            ),
          };
        }

        return {
          title: (
            <span style={{
              color: isLast ? '#333' : '#666',
              fontWeight: isLast ? 600 : 'normal',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}>
              {label}
              {isLast && guideButton}
            </span>
          ),
        };
      }
    }),
  ];

  return (
    <div style={{
      marginBottom: 16,
      paddingBottom: 16,
      borderBottom: '1px solid #d9d9d9',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
      <Breadcrumb
        separator={<span style={{ color: '#999', margin: '0 4px' }}>/</span>}
        items={breadcrumbItems}
      />
      {extra}
    </div>
  );
};

export default PageHeader;
